import type { Data } from '@puckeditor/core';
import { useQuery } from '@apollo/client';

import { CmsResolveContentDocument } from 'src/gql/graphql';

import { isPuckData } from './puck-utils';
import { useCmsVersionPreview } from './preview';

type CmsResolvedContentVariables = {
  dispensaryId?: string;
  enterpriseId: string | undefined;
  urlPath?: string;
};

type CmsResolvedContent = {
  data: Data | undefined;
  loading: boolean;
};

/**
 * Resolves published CMS content for a model via targeting (dispensaryId/urlPath), transparently
 * substituting a specific version's data instead when `cmsModelId`/`cmsVersionId` query params
 * match this model (see `useCmsVersionPreview`). Both fetch paths return untyped JSON on the
 * wire, so `data` is validated with `isPuckData` here regardless of which source served it --
 * callers get one `Data | undefined` shape either way, never a distinct "resolved" vs.
 * "versioned" content type.
 */
export function useCmsResolvedContent(modelId: string, variables: CmsResolvedContentVariables): CmsResolvedContent {
  const { dispensaryId, enterpriseId, urlPath } = variables;
  const preview = useCmsVersionPreview(modelId, enterpriseId);

  const { data: resolveData, loading: resolveLoading } = useQuery(CmsResolveContentDocument, {
    variables: { enterpriseId: enterpriseId ?? '', modelId, dispensaryId, urlPath },
    skip: !enterpriseId || preview.isPreviewing,
  });

  if (preview.isPreviewing) {
    return { data: preview.data, loading: preview.loading };
  }

  const data = resolveData?.cmsResolveContent?.data;
  return { data: isPuckData(data) ? data : undefined, loading: resolveLoading };
}
